import { ApolloLink, Observable } from "@apollo/client";
import { OperationDefinitionNode } from "graphql";
import { LOCAL_MODE } from "../../config/local";
import { CalendarConnectionInput, DayPart, GuestUpdateType, InboxItemType, InviteStatus, ListConnectionHubPagesQueryVariables, ListConnectionsByFilterQueryVariables, ListConnectionsQueryVariables, SeenStatus, SyncType } from "../../gql/types";
import { generateMockCaretakerDetails } from "../../mock/caretakerDetails";
import { generateMockCaretakerRequestWithFields, getMockCaretakerRequest } from "../../mock/createCaretakerRequest";
import { generateMockAcceptedResponse, generateMockCaretakerResponseWithFields, generateResponseWithResponseType } from "../../mock/createCaretakerResponse";
import { generateMockDigest } from "../../mock/generateDigest";
import { generateMockCalendarConnections, mockRemovedData } from "../../mock/getCalendarConnections";
import { generateMockCalendarsList } from "../../mock/getCalendarsList";
import { generateMockSyncStatus } from "../../mock/getSyncStatus";
import { generateMockUser } from "../../mock/getUser";
import { ListInboxInvitesVariables, mockListInboxInvites, mockRespondedData, mockUpdatedSeenData } from "../../mock/listInboxInvites";
import { ListPlaydateInvitesVariables, mockListPlaydateInvites, mockOutboundUpdatedSeenData } from "../../mock/listPlaydateInvites";
import { mockListConnections } from "../../mock/listConnections";
import { mockListConnectionHubPages } from "../../mock/listConnectionHubPages";
import { mockConnectionsByFilter } from "../../mock/listConnectionsByFilter";
import { generateMockNotification } from "../../mock/generateMockNotification";
import { getMockPlaydate } from "mock/createPlaydateInvite";
import { getUpdatedMockPlaydate } from "mock/updatePlaydateInvite";
import { generateMockSuggestions } from "mock/generateSuggestions";
import exampleFamily from "assets/exampleFamily.png";

export const mockGraphQLLink = new ApolloLink((operation, forward) => {
    if (!LOCAL_MODE) {
        return forward ? forward(operation) : new Observable(() => { });
    }

    return new Observable(observer => {
        const operationName = (operation.query.definitions.find(
            (def) => def.kind === "OperationDefinition"
        ) as OperationDefinitionNode)?.name?.value;

        if (!operationName) {
            console.warn("MockLink: No operation name found");
            if (forward) return forward(operation).subscribe(observer); // Use subscribe instead of returning forward
            return () => { }; // Always return a cleanup function
        }

        let response: { data: Record<string, any> } | null = null;
        const now = new Date().toISOString();

        switch (operationName) {
            case "GetSyncStatus":
                const syncType = operation.variables?.syncType;
                response = {
                    data: {
                        getSyncStatus: generateMockSyncStatus(syncType ?? SyncType.Initial),
                    }
                }
                break;
            case "GenerateDigest":
                response = {
                    data: {
                        generateDigest: generateMockDigest(),
                    }
                };
                break;

            case "GetUser":
                response = {
                    data: {
                        getUser: generateMockUser(),
                    }
                }
                break;

            case "GetCaretakerDetails":
                response = {
                    data: {
                        getCaretakerDetails: generateMockCaretakerDetails(),
                    }
                }
                break;

            case "CreateCaretakerRequest":
                const requestInput = operation.variables?.input;
                response = {
                    data: {
                        createCaretakerRequest: generateMockCaretakerRequestWithFields(requestInput),
                    }
                };
                break;

            case "CreateCaretakerResponse":
                const responseInput = operation.variables?.input;
                response = {
                    data: {
                        createCaretakerResponse: generateMockCaretakerResponseWithFields(responseInput),
                    }
                };
                break;

            case "UpdateCaretakerResponse":
                const updateResponseInput = operation.variables?.input;
                response = {
                    data: {
                        updateCaretakerResponse: generateResponseWithResponseType(updateResponseInput?.newResponseType),
                    }
                };
                break;

            case "UpdateInboxSeenStatus":
                const updateInput = operation.variables;
                response = {
                    data: {
                        updateInboxSeenStatus: {
                            success: true,
                        }
                    },
                };

                switch (updateInput?.itemType) {
                    case InboxItemType.InboundPlaydate:
                        mockUpdatedSeenData.push(updateInput?.input);
                        break;
                    case InboxItemType.OutboundPlaydate:
                        mockOutboundUpdatedSeenData.push(updateInput?.input);
                        break;
                }
                break;

            case "ListInboxInvites":
                response = mockListInboxInvites(operation.variables as ListInboxInvitesVariables);
                break;

            case "ListPlaydateInvites":
                response = mockListPlaydateInvites(operation.variables as ListPlaydateInvitesVariables);
                break;

            case "Logout":
                response = {
                    data: {
                        logout: {
                            success: true,
                        }
                    }
                };
                break;

            case "GetCaretakerRequest":
                const token = operation.variables?.token;
                const request = getMockCaretakerRequest(token);
                const caretakerResponse = generateMockAcceptedResponse();
                response = {
                    data: {
                        getCaretakerRequest: {
                            ...request,
                            filteredResponses: request ? [caretakerResponse] : [],
                        }
                    }
                };
                break;

            case "GetCalendarConnections":
                response = {
                    data: {
                        getCalendarConnections: generateMockCalendarConnections(),
                    }
                }
                break;

            case "GenerateSuggestions":
                response = {
                    data: {
                        generateSuggestions: generateMockSuggestions(),
                    }
                }
                break;

            case "CreatePlaydateInvite":
                const input = operation.variables?.input;

                response = {
                    data: {
                        createPlaydateInvite: getMockPlaydate(input),
                    }
                }
                break;

            case "UpdatePlaydateInvite":
                const updateInviteInput = operation.variables?.input;

                response = {
                    data: {
                        updatePlaydateInvite: getUpdatedMockPlaydate(updateInviteInput),
                    }
                }
                break;

            case "SaveUserSpouseEmail":
                const spouseEmailInput = operation.variables?.spouseEmailInput;
                response = {
                    data: {
                        saveUserSpouseEmail: {
                            __typename: "User",
                            id: "user123",
                            email: "user@example.com",
                            spouseEmail: spouseEmailInput,
                            matchedFamily: {
                                __typename: "FamilySummary",
                                id: "mock-family-id",
                                name: "Mock Family",
                                parents: [
                                    {
                                        __typename: "UserSummary",
                                        id: "parent1",
                                        profilePictureUrl: exampleFamily,
                                        name: "Sam",
                                    },
                                ],
                            },
                        }
                    }
                };
                break;

            case "AddUserToFamily":
                const familyID = operation.variables?.familyID;

                response = {
                    data: {
                        addUserToFamily: {
                            __typename: "User",
                            id: "user123",
                            email: "user@example.com",
                            firstName: "Mock",
                            lastName: "User",
                            profilePictureUrl: "https://example.com/mock-profile.jpg",
                            spouseEmail: "partner@example.com",
                            family: {
                                __typename: "FamilySummary",
                                familyID,
                                name: "Mock Family",
                            },
                            matchedFamily: null,
                            onboardingStatus: {
                                __typename: "OnboardingStatus",
                                status: "COMPLETE",
                            },
                            spouse: {
                                __typename: "UserSummary",
                                id: "spouse123",
                                firstName: "Partner",
                                lastName: "Example",
                                email: "partner@example.com",
                                profilePictureUrl: "https://example.com/spouse.jpg"
                            }
                        }
                    }
                };
                break;


            case "RemoveCalendarConnection":
                const removeConnInput = operation.variables?.calendar as CalendarConnectionInput;

                response = {
                    data: {
                        removeCalendarConnection: {
                            success: true,
                        }
                    },
                };
                mockRemovedData.push({
                    __typename: 'CalendarConnection',
                    source: removeConnInput.source,
                    identifier: removeConnInput.identifier,
                    label: removeConnInput.label ?? "",
                    expired: false,
                });
                break;

            case "AddExternalCalendars":
                response = {
                    data: {
                        addExternalCalendars: {
                            success: true,
                        }
                    }
                };
                break;

            case "GetCalendarsList":
                response = {
                    data: {
                        getUserExternalCalendarList: generateMockCalendarsList(),
                    }
                }
                break;

            case "CreatePlaydateInviteResponse":
                const playdateResponseInput = operation.variables?.input;
                response = {
                    data: {
                        createPlaydateInviteResponse: {
                            __typename: "PlaydateInviteResponse",
                            responseID: "mock-playdate-response-id",
                            inviteID: playdateResponseInput?.inviteID,
                            guest: {
                                __typename: "UserSummary",
                                id: "guest-123",
                                firstName: "Mock",
                                lastName: "Guest",
                                profilePictureUrl: "",
                                email: "mock.guest@example.com"
                            },
                            invite: {
                                __typename: "PlaydateInvite",
                                inviteID: playdateResponseInput?.inviteID || "1",
                                createdAt: now,
                                updatedAt: now,
                                title: "Mock Playdate",
                                location: "Central Park",
                                playdateType: "PRIVATE",
                                scheduleOptionType: "ONE_TIME",
                                scheduleOptions: [
                                    {
                                        __typename: "ScheduleOption",
                                        date: "2025-03-01T12:00:00Z",
                                        dayOfWeek: null,
                                        startTime: null,
                                        endTime: null,
                                        dayPart: DayPart.Morning,
                                    },
                                    {
                                        __typename: "ScheduleOption",
                                        date: "2025-03-01T12:00:00Z",
                                        dayOfWeek: null,
                                        startTime: null,
                                        endTime: null,
                                        dayPart: DayPart.EarlyAfternoon,
                                    },
                                ],
                                guestType: "PARENT",
                                rsvpDeadline: now,
                                details: "Mock playdate invite",
                                organizer: {
                                    __typename: "UserSummary",
                                    id: "organizer-123",
                                    firstName: "Organizer",
                                    lastName: "One",
                                    profilePictureUrl: "",
                                    email: "organizer@example.com"
                                },
                                guests: [],
                                externalGuests: [],
                                status: "IN_PLANNING",
                                recurrenceStartDate: null,
                                finalScheduleOption: [],
                                guestUpdate: {
                                    __typename: "PlaydateInviteGuestUpdate",
                                    updateID: "1:update1",
                                    inviteID: "1",
                                    guestID: playdateResponseInput?.guestID || "guest-123",
                                    updateType: GuestUpdateType.ResponseRecorded,
                                    message: "New invite",
                                    seenState: "READ",
                                    createdAt: now,
                                    updatedAt: now,
                                    responseID: null,
                                },
                                organizerUpdate: null,
                            },
                            externalGuest: null,
                            responseType: playdateResponseInput?.responseType,
                            selectedScheduleOptions: playdateResponseInput?.selectedScheduleOptions || [],
                            message: playdateResponseInput?.message || "",
                            createdAt: now,
                            updatedAt: now,
                        }
                    }
                };
                mockRespondedData.push({ inviteID: playdateResponseInput?.inviteID, guestID: playdateResponseInput?.guestID });
                break;

            case "UpdatePlaydateInviteResponse":
                const updatePlaydateResponseInput = operation.variables?.input;
                response = {
                    data: {
                        updatePlaydateInviteResponse: {
                            __typename: "PlaydateInviteResponse",
                            responseID: "mock-playdate-response-id",
                            inviteID: updatePlaydateResponseInput?.inviteID,
                            guest: {
                                __typename: "UserSummary",
                                id: "guest-123",
                                firstName: "Mock",
                                lastName: "Guest",
                                profilePictureUrl: "",
                                email: "mock.guest@example.com"
                            },
                            invite: {
                                __typename: "PlaydateInvite",
                                inviteID: updatePlaydateResponseInput?.inviteID || "1",
                                createdAt: now,
                                updatedAt: now,
                                title: "Mock Playdate",
                                location: "Central Park",
                                playdateType: "PRIVATE",
                                scheduleOptionType: "ONE_TIME",
                                scheduleOptions: [
                                    {
                                        __typename: "ScheduleOption",
                                        date: "2025-03-01T12:00:00Z",
                                        dayOfWeek: null,
                                        startTime: null,
                                        endTime: null,
                                        dayPart: DayPart.Morning,
                                    },
                                    {
                                        __typename: "ScheduleOption",
                                        date: "2025-03-01T12:00:00Z",
                                        dayOfWeek: null,
                                        startTime: null,
                                        endTime: null,
                                        dayPart: DayPart.EarlyAfternoon,
                                    },
                                ],
                                guestType: "PARENT",
                                rsvpDeadline: now,
                                details: "Mock playdate invite",
                                organizer: {
                                    __typename: "UserSummary",
                                    id: "organizer-123",
                                    firstName: "Organizer",
                                    lastName: "One",
                                    profilePictureUrl: "",
                                    email: "organizer@example.com"
                                },
                                guests: [],
                                externalGuests: [],
                                status: "IN_PLANNING",
                                recurrenceStartDate: null,
                                finalScheduleOption: [],
                                guestUpdate: {
                                    __typename: "PlaydateInviteGuestUpdate",
                                    updateID: "1:update1",
                                    inviteID: "1",
                                    guestID: updatePlaydateResponseInput?.guestID || "guest-123",
                                    updateType: GuestUpdateType.ResponseRecorded,
                                    message: "New invite",
                                    seenState: "READ",
                                    createdAt: now,
                                    updatedAt: now,
                                    responseID: null,
                                },
                                organizerUpdate: null,
                            },
                            externalGuest: null,
                            responseType: updatePlaydateResponseInput?.responseType,
                            selectedScheduleOptions: updatePlaydateResponseInput?.selectedScheduleOptions || [],
                            message: updatePlaydateResponseInput?.message || "",
                            createdAt: now,
                            updatedAt: now,
                        }
                    }
                };
                break;

            case "DeletePlaydateInvite":
                const deleteInviteInput = operation.variables?.input;
                response = {
                    data: {
                        deletePlaydateInvite: {
                            __typename: "PlaydateInvite",
                            status: InviteStatus.Abandoned,
                            inviteID: operation.variables?.inviteID,
                        },
                    },
                };
                break;

            case "ListConnections":
                response = mockListConnections(operation.variables as ListConnectionsQueryVariables);
                break;

            case "ListConnectionHubPages":
                response = mockListConnectionHubPages(operation.variables as ListConnectionHubPagesQueryVariables);
                break;

            case "ListConnectionsByFilter":
                response = mockConnectionsByFilter(operation.variables as ListConnectionsByFilterQueryVariables);
                break;

            case "Notifications": {
                const { limit = 10, cursor } = operation.variables || {};

                const cursorOffset = cursor?.startsWith("cursor-")
                    ? parseInt(cursor.replace("cursor-", ""), 10)
                    : 0;

                const totalNotifications = 50;

                const remaining = totalNotifications - cursorOffset;
                const pageSize = Math.min(limit, remaining);

                const newOffset = cursorOffset + pageSize;

                const edges = Array.from({ length: pageSize }, (_, i) => {
                    const index = cursorOffset + i;
                    return {
                        __typename: "NotificationEdge",
                        cursor: `cursor-${index}`,
                        node: generateMockNotification(index),
                    };
                });

                const hasNextPage = newOffset < totalNotifications;

                response = {
                    data: {
                        notifications: {
                            __typename: "NotificationConnection",
                            edges,
                            pageInfo: {
                                __typename: "PageInfo",
                                endCursor: hasNextPage ? `cursor-${newOffset}` : null,
                                hasNextPage,
                            },
                        },
                    },
                };
                break;
            }

            default:
                console.warn(`No mock response defined for: ${operationName}`);
                if (forward) return forward(operation).subscribe(observer); // Ensure forward calls subscribe
                return () => { }; // Always return a valid function
        }

        // Ensure response is never null before calling `observer.next()`
        setTimeout(() => {
            if (response) {
                observer.next(response);
                observer.complete();
            } else {
                console.error(`🚨 MockLink Error: response is null for operation ${operationName}`);
                observer.error(new Error(`Missing mock response for operation: ${operationName}`));
            }
        }, 200);

        // Return a cleanup function to satisfy the expected type
        return () => console.log(`MockLink completed: ${operationName}`);
    });
});
